import { useState, useEffect, useCallback } from 'react';
import classroom1 from '@/images/Classroom-1.jpg';
import classroom2 from '@/images/Classroom-2.jpg';
import classroom3 from '@/images/Classroom-3.jpg';

const images = [
  { src: classroom1, alt: 'Classroom Memory 1' },
  { src: classroom2, alt: 'Classroom Memory 2' },
  { src: classroom3, alt: 'Classroom Memory 3' },
];

const AUTO_ROTATE_INTERVAL = 5000;

export function BannerCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const hasMultiple = images.length > 1;

  const goTo = useCallback((index: number) => {
    setActiveIndex((index + images.length) % images.length);
  }, []);

  useEffect(() => {
    if (!hasMultiple) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, AUTO_ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [hasMultiple]);

  return (
    <section className="py-8 px-4 bg-secondary relative overflow-hidden">
      {/* Subtle background gradient */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background:
            'linear-gradient(135deg, rgba(233, 69, 96, 0.05) 0%, transparent 40%, rgba(233, 69, 96, 0.08) 100%)',
        }}
      />

      <div className="container mx-auto max-w-6xl relative z-10 flex flex-col items-center">
        {/* Carousel viewport */}
        <div
          className="relative w-1/2 rounded-2xl overflow-hidden opacity-0 animate-fade-in-up"
          style={{
            animationFillMode: 'forwards',
            border: '1px solid rgba(233, 69, 96, 0.15)',
            boxShadow: '0 0 40px rgba(233, 69, 96, 0.1)',
          }}
        >
          {/* Slides */}
          {images.map((image, index) => (
            <div
              key={index}
              className={`w-full transition-opacity duration-500 ${
                index === activeIndex ? 'opacity-100' : 'opacity-0 absolute inset-0'
              }`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-auto block"
              />
            </div>
          ))}

          {/* Prev/Next arrows — only when multiple images */}
          {hasMultiple && (
            <>
              <button
                onClick={() => goTo(activeIndex - 1)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center bg-black/40 hover:bg-black/60 text-white transition-colors"
                aria-label="Previous slide"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => goTo(activeIndex + 1)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center bg-black/40 hover:bg-black/60 text-white transition-colors"
                aria-label="Next slide"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Navigation dots — only when multiple images */}
        {hasMultiple && (
          <div className="flex items-center justify-center gap-2 mt-4">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === activeIndex
                    ? 'w-6 bg-romantic-rose'
                    : 'w-2 bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
